import React, { useState, useEffect } from "react";
import {
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  FormFeedback,
  Row,
  Col,
} from "reactstrap";
import { CREATE_NEW_SALES_REP_LEAD } from "../../redux/Action/actionTypes";
import { useDispatch } from "react-redux";

// Define the interface for SalesRepLead object
export interface SalesRepLead {
  Name: string;
  EmailAddress?: string | null;
  MobileNumber?: string | null;
  [key: string]: any; // Allow additional fields dynamically
}

// Component to handle the creation or update of a Sales Representative Lead
const NewSalesRepLeadForm = ({
  onCancel, // Callback function to handle cancel action
  salesRepLead, // Existing sales rep lead data for edit mode
  newSupplierID, // Supplier ID for new lead creation
  supplierName, // Supplier name to display
}: {
  onCancel: () => void;
  salesRepLead?: SalesRepLead | null;
  newSupplierID?: string | null;
  supplierName?: string | null;
}) => {
  // State variables to manage form data
  const [id, setId] = useState(salesRepLead?.id || "");
  const [name, setName] = useState(salesRepLead?.Name ?? "");
  const [email, setEmail] = useState(salesRepLead?.emailAddress || "");
  const [mobile, setMobile] = useState(salesRepLead?.mobileNumber || "");
  const [supplierId, setSupplierId] = useState(salesRepLead?.supplierID || newSupplierID || "");
  
  // State to track which fields have been interacted with
  const [touched, setTouched] = useState({
    name: false,
    email: false,
    mobile: false,
    supplierId: false,
  });

  // Redux dispatch function to trigger actions
  const dispatch = useDispatch();

  // Feedback message for email validation
  let emailFeedbackMessage = "";
  if (email.length === 0) {
    emailFeedbackMessage = "Email address is required.";
  } else if (email.length > 100) {
    emailFeedbackMessage = "Max 100 characters.";
  } else {
    emailFeedbackMessage = "Please enter a valid email address.";
  }

  // Effect to update state when salesRepLead prop changes (useful for edit mode)
  useEffect(() => {
    if (salesRepLead) {
      setId(salesRepLead.value || "");
      setName(salesRepLead.Name || "");
      setEmail(salesRepLead.emailAddress || "");
      setMobile(salesRepLead.mobileNumber || "");
      setSupplierId(salesRepLead.supplierID || "");
    }
  }, [salesRepLead]);

  // Validation for name field
  const isNameInvalid =
    name.length > 50 || (touched.name && name.trim() === "");

  // Validation for email field
  const isEmailInvalid =
    touched.email &&
    (email.trim() === "" || !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email));

  // Function to handle saving the form data
  const handleSave = () => {
    // Prevent saving if any validation fails
    if (isNameInvalid || isEmailInvalid || mobile.length === 0) return;

    // Construct the new sales rep lead object
    const newSalesRepLead = {
      ID: id || null,
      Name: name.trim(),
      EmailAddress: email.trim() || null,
      MobileNumber: mobile.trim() || null,
      SupplierID: supplierId.trim() || null,
    };

    // Dispatch the action to save the lead
    dispatch({ type: CREATE_NEW_SALES_REP_LEAD, payload: { newSalesRepLead } });

    // Call the cancel callback after saving
    onCancel();
  };

  // Render the form component
  return (
    <div className="mb-3 mt-3 p-3 border rounded bg-light">
      {/* Display supplier name */}
      <div className="mb-2">
        <strong>Supplier name : {supplierName}</strong>
      </div>
      <Form>
        <Row>
          {/* Name input field */}
          <Col md={4}>
            <FormGroup className="mb-2">
              <Label for="name" className="mb-1">
                * Name:
              </Label>
              <Input
                type="text"
                id="name"
                bsSize="sm"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={() => setTouched((prev) => ({ ...prev, name: true }))}
                invalid={isNameInvalid}
              />
              <FormFeedback>
                {name.length > 50 ? "Max 50 characters." : "Name is required."}
              </FormFeedback>
            </FormGroup>
          </Col>

          {/* Email input field */}
          <Col md={4}>
            <FormGroup className="mb-2">
              <Label for="email" className="mb-1">
                * Email Address:
              </Label>
              <Input
                type="email"
                id="email"
                bsSize="sm"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => setTouched((prev) => ({ ...prev, email: true }))}
                invalid={email.length > 100 || isEmailInvalid}
              />
              <FormFeedback>
                {emailFeedbackMessage}
              </FormFeedback>
            </FormGroup>
          </Col>

          {/* Mobile number input field */}
          <Col md={4}>
            <FormGroup className="mb-2">
              <Label for="mobile" className="mb-1">
                * Mobile Number:
              </Label>
              <Input
                type="tel"
                id="mobile"
                bsSize="sm"
                value={mobile}
                pattern="[0-9]*"
                inputMode="numeric"
                onChange={(e) => {
                  // Allow only numeric characters and specific symbols
                  const allowedChars = e.target.value.replace(/[^0-9+\-\s]/g, "");
                  setMobile(allowedChars);
                }}
                onBlur={() => setTouched((prev) => ({ ...prev, mobile: true }))}
                invalid={mobile.length > 15 || mobile.length === 0}
              />
              <FormFeedback>
                {mobile.length === 0
                  ? "Mobile number is required."
                  : "Max 15 digits are allowed."}
              </FormFeedback>
            </FormGroup>
          </Col>
        </Row>

        {/* Action buttons */}
        <div className="d-flex gap-2 mt-2">
          <Button color="secondary" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            color="primary"
            size="sm"
            onClick={handleSave}
            disabled={!name.trim() || isNameInvalid || isEmailInvalid || mobile.length === 0}
          >
            {salesRepLead ? "Update" : "Save"}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default NewSalesRepLeadForm;